import { BadgeVenta, VENTA_META } from '@/components/BadgeVenta';
import { useMatriz } from '@/hooks/useMatriz';
import { cn } from '@/lib/utils';
import type { EstadoVenta, MatrizPartida } from '@adelante/shared';
import { CircleNotch, MagnifyingGlass, Timer } from '@phosphor-icons/react';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Mapa de calor del % de avance: lime suave (avance bajo) → lime fuerte (100%).
 * 0% gris claro, "no aplica" casi blanco.
 */
function estiloCelda(v: number | null): CSSProperties {
  if (v === null) return { background: '#fafafa', color: '#d4d4d8' };
  if (v <= 0) return { background: '#f4f4f5', color: '#a1a1aa' };
  const alpha = 0.12 + (Math.min(v, 100) / 100) * 0.88; // 0.12 → 1
  return { background: `rgba(173, 208, 16, ${alpha})`, color: '#18181b' };
}

/** Columnas resumen (Crono / Gen) — heatmap AZUL para distinguirlas del lime. */
function estiloResumen(v: number | null): CSSProperties {
  if (v === null || v <= 0) return { background: '#eff6ff', color: '#94a3b8' };
  const alpha = 0.16 + (Math.min(v, 100) / 100) * 0.74; // 0.16 → 0.9
  return { background: `rgba(59, 130, 246, ${alpha})`, color: '#0f172a' };
}

interface Props {
  proyecto: string | null;
  /** Semana operativa (id) para ver la FOTO histórica del cierre. Sin ella = vivo. */
  semana?: number | null;
  /** Modo reporte: solo lectura, sin click-a-capturar. */
  readonly?: boolean;
}

/**
 * Vista Matriz "Por Costos": filas = obras habilitadas, columnas = partidas
 * (agrupadas por grupo). Celda = promedio del % de las sub-partidas de esa
 * partida, coloreada como mapa de calor. Click en una fila → captura de la
 * obra. Usa todo el ancho de pantalla (full-bleed) con scroll horizontal y la
 * columna de obra fija.
 */
export function MatrizAvanceVista({ proyecto, semana, readonly = false }: Props) {
  const { data, isLoading, isError, refetch } = useMatriz(proyecto, undefined, semana);
  const navigate = useNavigate();
  // En modo captura (no readonly) recordamos buscador + filtro de venta en
  // sessionStorage: al entrar a una obra y volver, el filtro sigue puesto. En
  // modo reporte (readonly) NO se persiste (no se navega a captura).
  const [busqueda, setBusqueda] = useState(() =>
    readonly ? '' : sessionStorage.getItem('campo.matriz.busqueda') || '',
  );
  const [ventaFiltro, setVentaFiltro] = useState<EstadoVenta | null>(() => {
    if (readonly) return null;
    const v = sessionStorage.getItem('campo.matriz.ventaFiltro');
    return v === 'formalizada' || v === 'reservada' || v === 'disponible' || v === 'entregada'
      ? v
      : null;
  });
  useEffect(() => {
    if (readonly) return;
    sessionStorage.setItem('campo.matriz.busqueda', busqueda);
  }, [busqueda, readonly]);
  useEffect(() => {
    if (readonly) return;
    if (ventaFiltro) sessionStorage.setItem('campo.matriz.ventaFiltro', ventaFiltro);
    else sessionStorage.removeItem('campo.matriz.ventaFiltro');
  }, [ventaFiltro, readonly]);
  const matriz = data?.data;

  // Tramos de grupo (partidas consecutivas que comparten grupo) para el header.
  const grupos = useMemo(() => {
    const out: { codigo: string | null; nombre: string | null; span: number }[] = [];
    for (const p of matriz?.partidas ?? []) {
      const ultimo = out[out.length - 1];
      if (ultimo && ultimo.codigo === p.grupo_codigo) ultimo.span += 1;
      else out.push({ codigo: p.grupo_codigo, nombre: p.grupo_nombre, span: 1 });
    }
    return out;
  }, [matriz]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando matriz…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-2 py-8 text-center text-sm text-muted-foreground">
        <p>No se pudo cargar la matriz.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-adelante-primary-dark underline"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!matriz || matriz.obras.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        {proyecto
          ? 'No hay obras en ejecución en este proyecto.'
          : 'No hay obras en ejecución para mostrar.'}
      </p>
    );
  }

  const { partidas, obras } = matriz;
  const q = busqueda.trim().toLowerCase();
  const obrasTexto = q ? obras.filter((o) => o.codigo.toLowerCase().includes(q)) : obras;
  // Conteo por estado de venta (sobre el filtro de texto) para los chips.
  const ventaConteos = obrasTexto.reduce(
    (acc, o) => {
      if (o.estado_venta) acc.conteos[o.estado_venta]++;
      return acc;
    },
    {
      conteos: { formalizada: 0, reservada: 0, disponible: 0, entregada: 0 } as Record<
        EstadoVenta,
        number
      >,
      total: obrasTexto.length,
    },
  );
  const obrasFiltradas = ventaFiltro
    ? obrasTexto.filter((o) => o.estado_venta === ventaFiltro)
    : obrasTexto;
  const irACaptura = (codigo: string) => navigate(`/campo/avance/${encodeURIComponent(codigo)}`);
  // Click en una celda de partida → captura enfocada en esa partida.
  const irAPartida = (codigo: string, partidaCodigo: string) =>
    navigate(
      `/campo/avance/${encodeURIComponent(codigo)}?partida=${encodeURIComponent(partidaCodigo)}`,
    );

  // Encabezado fijo arriba (2 filas) + columna de obra fija a la izquierda. El
  // contenedor tiene su propio scroll (x e y) para que el sticky funcione.
  const thBase = 'h-7 bg-zinc-100 px-1 text-[11px] font-medium text-muted-foreground';
  const cellBase = 'border-b border-l border-border px-1 py-1 text-center text-xs tabular-nums';

  return (
    // full-bleed: usa todo el ancho de la ventana, no el max-w-md del layout campo.
    <div className="relative left-1/2 w-screen -translate-x-1/2 px-4">
      {/* Buscador de obra */}
      <div className="mb-2 flex items-center gap-2">
        <div className="relative">
          <MagnifyingGlass
            className="-translate-y-1/2 absolute top-1/2 left-2 h-4 w-4 text-muted-foreground"
            weight="bold"
            aria-hidden="true"
          />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar obra…"
            className="h-8 w-64 rounded-md border border-border pr-2 pl-8 text-sm"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {obrasFiltradas.length} obra{obrasFiltradas.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Filtro por estado de venta */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setVentaFiltro(null)}
          className={cn(
            'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            ventaFiltro === null
              ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
              : 'border-border bg-white text-muted-foreground',
          )}
        >
          Todas
          <span className="ml-1 tabular-nums opacity-70">{ventaConteos.total}</span>
        </button>
        {(['formalizada', 'reservada', 'disponible', 'entregada'] as const).map((ev) => {
          const m = VENTA_META[ev];
          const activo = ventaFiltro === ev;
          return (
            <button
              key={ev}
              type="button"
              onClick={() => setVentaFiltro(activo ? null : ev)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                activo ? m.chip : 'border-border bg-white text-muted-foreground',
              )}
            >
              <span className={cn('inline-block h-2 w-2 rounded-full', m.dot)} aria-hidden="true" />
              {m.label}
              <span className="tabular-nums opacity-70">{ventaConteos.conteos[ev]}</span>
            </button>
          );
        })}
      </div>

      <div
        className="overflow-auto rounded-md border border-border"
        style={{ maxHeight: 'calc(100vh - 240px)' }}
      >
        <table
          className="table-fixed border-collapse"
          style={{ width: 116 + (partidas.length + 2) * 56 }}
        >
          <colgroup>
            <col style={{ width: 116 }} />
            {partidas.map((p) => (
              <col key={p.id} style={{ width: 56 }} />
            ))}
            <col style={{ width: 56 }} />
            <col style={{ width: 56 }} />
          </colgroup>
          <thead>
            {/* Fila de grupos (sticky arriba). Crono+Gen van al final como "Resumen". */}
            <tr>
              <th className={cn(thBase, 'sticky top-0 left-0 z-30 border-b border-border')} />
              {grupos.map((g, i) => (
                <th
                  key={`${g.codigo ?? 'sin'}-${i}`}
                  colSpan={g.span}
                  className={cn(
                    thBase,
                    'sticky top-0 z-20 border-b border-l border-border text-center uppercase tracking-wide',
                  )}
                >
                  {g.nombre ?? '—'}
                </th>
              ))}
              <th
                colSpan={2}
                className={cn(
                  thBase,
                  'sticky top-0 z-20 border-b border-l border-border bg-blue-50 text-center text-blue-700 uppercase tracking-wide',
                )}
              >
                Resumen
              </th>
            </tr>
            {/* Fila de columnas (sticky bajo la de grupos). Títulos horizontales
                truncados; el nombre completo aparece en el tooltip (title). */}
            <tr>
              <th
                className={cn(thBase, 'sticky top-7 left-0 z-30 border-b border-border text-left')}
              >
                Obra
              </th>
              {partidas.map((p: MatrizPartida) => (
                <th
                  key={p.id}
                  className={cn(
                    thBase,
                    'sticky top-7 z-20 overflow-hidden border-b border-l border-border',
                  )}
                  title={`${p.codigo} · ${p.nombre}`}
                >
                  <span className="block truncate font-normal text-[10px] leading-tight">
                    {p.nombre}
                  </span>
                </th>
              ))}
              <th
                className={cn(
                  thBase,
                  'sticky top-7 z-20 border-b border-l border-border bg-blue-50 text-blue-700',
                )}
                title="Avance del sprint (crono)"
              >
                <Timer className="mx-auto h-3.5 w-3.5" weight="bold" aria-label="Crono" />
              </th>
              <th
                className={cn(
                  thBase,
                  'sticky top-7 z-20 border-b border-l border-border bg-blue-50 text-blue-700',
                )}
              >
                Gen
              </th>
            </tr>
          </thead>
          <tbody>
            {obrasFiltradas.map((o) => (
              <tr
                key={o.codigo}
                onClick={readonly ? undefined : () => irACaptura(o.codigo)}
                onKeyDown={
                  readonly
                    ? undefined
                    : (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          irACaptura(o.codigo);
                        }
                      }
                }
                tabIndex={readonly ? undefined : 0}
                className={cn(
                  'hover:bg-zinc-50',
                  !readonly && 'cursor-pointer',
                  o.congelada && 'opacity-50',
                )}
                title={o.congelada ? `${o.codigo} — congelada (en espera por NC)` : undefined}
              >
                <td className="sticky left-0 z-10 overflow-hidden border-b border-border bg-white px-2 py-1">
                  <div className="flex items-center gap-1">
                    <span className="truncate font-mono text-xs font-semibold">{o.codigo}</span>
                    <BadgeVenta estado={o.estado_venta} className="h-4 w-4 text-[9px]" />
                    {o.congelada && <span className="text-[9px] text-violet-500">❄</span>}
                  </div>
                </td>
                {partidas.map((p) => {
                  const v = o.celdas[p.id] ?? null;
                  return readonly ? (
                    <td
                      key={p.id}
                      className={cn(cellBase)}
                      style={estiloCelda(v)}
                      title={`${o.codigo} · ${p.codigo} ${p.nombre}`}
                    >
                      {v === null ? '—' : v}
                    </td>
                  ) : (
                    <td
                      key={p.id}
                      className="overflow-hidden border-b border-l border-border p-0"
                      style={estiloCelda(v)}
                    >
                      <button
                        type="button"
                        title={`${o.codigo} · ${p.codigo} ${p.nombre} — abrir esta partida`}
                        onClick={(e) => {
                          e.stopPropagation();
                          irAPartida(o.codigo, p.codigo);
                        }}
                        className="block h-full w-full px-1 py-1 text-center text-xs tabular-nums text-inherit hover:ring-2 hover:ring-adelante-primary hover:ring-inset"
                      >
                        {v === null ? '—' : v}
                      </button>
                    </td>
                  );
                })}
                <td className={cn(cellBase, 'font-semibold')} style={estiloResumen(o.avance_crono)}>
                  {o.avance_crono}
                </td>
                <td
                  className={cn(cellBase, 'font-semibold')}
                  style={estiloResumen(o.avance_general)}
                >
                  {o.avance_general}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        ⏱ = % crono acumulado · cada celda = % de la partida (pasá el mouse por el título para ver
        el nombre completo)
        {readonly
          ? ' · foto del cierre de la semana (solo lectura).'
          : ' · tocá una celda para abrir esa partida, o el código de obra para la captura.'}
      </p>
    </div>
  );
}
